/**
 * @fileOverview This file contains the implementation of the ReportViewer component for the Reports builder
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region Imports
import '../../scss/_pdfme.scss';
import React, { Component } from 'react';
import { Viewer } from '@pdfme/ui';
import { text, image, line, rectangle, ellipse, table } from '@pdfme/schemas';
import RobotoR from '../../media/Fonts/Roboto/Roboto-Regular.ttf';
import RobotoB from '../../media/Fonts/Roboto/Roboto-Bold.ttf';
import RobotoI from '../../media/Fonts/Roboto/Roboto-Italic.ttf';
import RobotoBI from '../../media/Fonts/Roboto/Roboto-BoldItalic.ttf';
import NoticiaTextR from '../../media/Fonts/NoticiaText/NoticiaText-Regular.ttf';
import NoticiaTextB from '../../media/Fonts/NoticiaText/NoticiaText-Bold.ttf';
import NoticiaTextI from '../../media/Fonts/NoticiaText/NoticiaText-Italic.ttf';
import NoticiaTextBI from '../../media/Fonts/NoticiaText/NoticiaText-BoldItalic.ttf';
import InriaSerifR from '../../media/Fonts/InriaSerif/InriaSerif-Regular.ttf';
import InriaSerifB from '../../media/Fonts/InriaSerif/InriaSerif-Bold.ttf';
import InriaSerifI from '../../media/Fonts/InriaSerif/InriaSerif-Italic.ttf';
import InriaSerifBI from '../../media/Fonts/InriaSerif/InriaSerif-BoldItalic.ttf';
import { Box, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { getToast } from '../../utils/toastConfig';
//#endregion

/**
 * Generates the font URL based on the given path.
 * @param {string} path - The path to the font file.
 * @returns {string} The full URL to the font file.
 */
const getFontUrl = (fileName) => {
  const baseUrl = 'https://durandaldabia9bc.blob.core.windows.net/fonts/';
  return `${baseUrl}${fileName}`;
};

const font = {
  Roboto: {
    data: getFontUrl('Roboto-Regular.ttf'),
    fallback: true,
  },
  RobotoBold: {
    data: getFontUrl('Roboto-Bold.ttf'),
  },
  RobotoItalic: {
    data: getFontUrl('Roboto-Italic.ttf'),
  },
  RobotoBoldItalic: {
    data: getFontUrl('Roboto-BoldItalic.ttf'),
  },
  NoticiaText: {
    data: getFontUrl('NoticiaText-Regular.ttf'),
  },
  NoticiaTextBold: {
    data: getFontUrl('NoticiaText-Bold.ttf'),
  },
  NoticiaTextItalic: {
    data: getFontUrl('NoticiaText-Italic.ttf'),
  },
  NoticiaTextBoldItalic: {
    data: getFontUrl('NoticiaText-BoldItalic.ttf'),
  },
  InriaSerif: {
    data: getFontUrl('InriaSerif-Regular.ttf'),
  },
  InriaSerifBold: {
    data: getFontUrl('InriaSerif-Bold.ttf'),
  },
  InriaSerifItalic: {
    data: getFontUrl('InriaSerif-Italic.ttf'),
  },
  InriaSerifBoldItalic: {
    data: getFontUrl('InriaSerif-BoldItalic.ttf'),
  },
  Bahamas: {
    data: getFontUrl('bahamasn.TTF'),
  },
  BahamasBold: {
    data: getFontUrl('bahamasb.TTF'),
  },
};

class ReportViewer extends Component {
  componentDidMount() {
    this.renderViewer();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.template !== this.props.template ||
      prevProps.inputs !== this.props.inputs
    ) {
      this.renderViewer();
    }
  }

  /**
   * Renders the PDF viewer with the provided template and inputs.
   */
  renderViewer() {
    const { template, inputs } = this.props;

    if (template) {
      let finalTemplate = { ...template };

      // Equipment maintenance dynamic pages
      if (inputs && inputs[0] && inputs[0].__equipmentInstances__) {
        const schemas = [...template.schemas];
        const equipmentInstances = inputs[0].__equipmentInstances__;
        // Últimas 2 páginas del template: penúltima = fotos de equipo, última = evidencias adicionales
        const equipmentSchemaIndex = schemas.length - 2;
        const evidenceSchemaIndex = schemas.length - 1;
        const equipmentBaseSchema = schemas[equipmentSchemaIndex];
        const evidenceBaseSchema = schemas[evidenceSchemaIndex];

        const finalSchemas = schemas.slice(0, equipmentSchemaIndex);

        const firmasTecnico = inputs[0]['res_Firma técnico'] || '';
        const firmasCliente = inputs[0]['res_Firma cliente'] || '';
        const resTech = inputs[0]['res_tech'] || '';
        const resVisitId = inputs[0]['res_visitId'] || '';

        const fillCommonFields = (baseSchema, equipIndex) => {
          baseSchema.forEach((field) => {
            if (field.name.startsWith('res_Firma técnico')) {
              inputs[0][`${field.name} - ${equipIndex}`] = firmasTecnico;
            } else if (field.name.startsWith('res_Firma cliente')) {
              inputs[0][`${field.name} - ${equipIndex}`] = firmasCliente;
            } else if (field.name.startsWith('res_tech')) {
              inputs[0][`${field.name} - ${equipIndex}`] = resTech;
            } else if (field.name.startsWith('res_visitId')) {
              inputs[0][`${field.name} - ${equipIndex}`] = resVisitId;
            }
          });
        };

        equipmentInstances.forEach((equip, equipIndex) => {
          // Página de fotos del equipo, seguida inmediatamente por su página de evidencias adicionales
          if (equipmentBaseSchema) {
            finalSchemas.push(
              equipmentBaseSchema.map((field) => ({
                ...field,
                name: `${field.name} - ${equipIndex}`,
              })),
            );
            fillCommonFields(equipmentBaseSchema, equipIndex);
          }

          if (evidenceBaseSchema) {
            finalSchemas.push(
              evidenceBaseSchema.map((field) => ({
                ...field,
                name: `${field.name} - ${equipIndex}`,
              })),
            );
            fillCommonFields(evidenceBaseSchema, equipIndex);
          }
        });

        delete inputs[0].__equipmentInstances__;
        finalTemplate = { ...template, schemas: finalSchemas };
      }

      this.viewer = new Viewer({
        domContainer: this.domContainer,
        template: finalTemplate,
        inputs: inputs || [{}],
        plugins: {
          text,
          image,
          line,
          rectangle,
          ellipse,
          table,
        },
        options: {
          font: font,
        },
      });
    }
  }

  render() {
    return (
      <Box className="report-viewer-container">
        <Box className="report-viewer-btns-container">
          <IconButton
            className="report-viewer-download-icon-btn"
            onClick={this.props.downloadReport}
          >
            <FontAwesomeIcon icon={faFileDownload} />
          </IconButton>
          <IconButton
            className="report-viewer-close-icon-btn"
            onClick={this.props.onClose}
          >
            <FontAwesomeIcon icon={faClose} />
          </IconButton>
        </Box>
        <Box
          sx={{
            height: '100% !important',
            width: '100% !important',
          }}
          ref={(domContainer) => {
            this.domContainer = domContainer;
          }}
        />
      </Box>
    );
  }
}

export default ReportViewer;
